import React, { useMemo, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppSelector } from '../../../../store/hooks';
import screeningIcon from '../../../../assets/icons/screeningIcon.svg';
import { metaDataSelector } from '../../../../store/globalSelectors';
import { BMRSection, BMRTitle, BMRValue } from '../style';
import { glimConstants } from '../../../../shared/shared.constants';
import { setGlim } from '../../../../store/slices/metaDataSlice';
import { useDispatch } from 'react-redux';

export const ScreeningResult = () => {
  const dispatch = useDispatch();
  const {
    malnutritionScreening: { glim },
    patientInformation
  } = useAppSelector(metaDataSelector);
  const calculatedCircumVal = useMemo(() => {
    let calculatedCircum = 0;
    if (glim.isPatientHealthy === 'one') {
      glimConstants.calfCalc.healthy.forEach((item) => {
        if (
          patientInformation.bmi >= item[0] &&
          patientInformation.bmi <= item[1]
        ) {
          calculatedCircum = Number(glim.calfCircumference) + item[2];
        }
      });
    } else {
      glimConstants.calfCalc.nothealthy.forEach((item) => {
        if (
          patientInformation.bmi >= item[0] &&
          patientInformation.bmi <= item[1]
        ) {
          calculatedCircum = Number(glim.calfCircumference) + item[2];
        }
      });
    }
    if (glim.isPatientHasOedema) {
      calculatedCircum = calculatedCircum - 2;
    }

    if (patientInformation.gender === 'female') {
      if (calculatedCircum < 33) {
        return 1;
      } else {
        return 0;
      }
    } else {
      if (calculatedCircum < 34) {
        return 1;
      } else {
        return 0;
      }
    }
  }, [
    glim.calfCircumference,
    glim.isPatientHealthy,
    patientInformation.bmi,
    glim.isPatientHasOedema,
    patientInformation.gender
  ]);

  const phenoResult = useMemo(() => {
    return (
      (glim.phenoCrtiteria ? 1 : 0) +
      (glim.bmiOption === 'one' ? 0 : 1) +
      (glim.reduceMuscleMass ? 1 : 0) +
      calculatedCircumVal
    );
  }, [
    glim.phenoCrtiteria,
    glim.bmiOption,
    glim.reduceMuscleMass,
    calculatedCircumVal
  ]);

  const etioResult = useMemo(() => {
    return (
      (glim.etiologicalCriteria.reducedNutritionIntake ||
      glim.etiologicalCriteria.isPatientHasIntestineIssue
        ? 1
        : 0) + (glim.etiologicalCriteria.isPatientInjured ? 1 : 0)
    );
  }, [glim.etiologicalCriteria]);

  useEffect(() => {
    dispatch(
      setGlim({
        ...glim,
        phenotypicScore: phenoResult,
        etiologicalscore: etioResult
      })
    );
  }, [phenoResult, etioResult]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        gap: '1.875rem',
        marginBottom: '1.875rem'
      }}
    >
      <Box
        sx={{
          width: '110px',
          height: '110px',
          borderRadius: '100px',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <img src={screeningIcon} alt="screeningIcon" />
      </Box>
      <Typography
        sx={{
          fontSize: '1.5625rem',
          lineHeight: '35px',
          fontFamily: 'BGBold',
          fontWeight: '450'
        }}
      >
        SCREENING RESULTS
      </Typography>
      <Box sx={{ width: '100%', backgroundColor: '#004F71' }}>
        <BMRSection className="wd-auto">
          <BMRTitle variant="h2">{'Phenotypic Criteria Score:'}</BMRTitle>
          <BMRValue>{phenoResult}</BMRValue>
        </BMRSection>
        <BMRSection className="wd-auto">
          <BMRTitle variant="h2">{'Etiological Criteria Score:'}</BMRTitle>
          <BMRValue>{etioResult}</BMRValue>
        </BMRSection>
      </Box>
      <Box
        sx={{
          width: '80%',
          backgroundColor: '#fff',
          border: '0.125rem solid #002A3A',
          borderRadius: '0.9375rem',
          padding: '0.9375rem',
          fontFamily: 'BGRegular'
        }}
      >
        Any one phenotypic criteria combined with any one etiological criteria{' '}
        <strong>may indicate malnutrition.</strong>
      </Box>
    </Box>
  );
};
